import React, { useState } from "react";
import { X } from "lucide-react";
import { companyBankTransferUseCase } from "../../../../di/container";
import type { BankTransferEntity } from "../../../../domain/types/company-bank-transfer";
import { BANKS, Field, ModalShell, inputClass } from "./shared";

/**
 * 수기 확인 계좌 등록·수정.
 *
 * 받는 것은 고객에게 보여줄 값뿐이다 — 은행·계좌번호·예금주. **비밀번호는 받지 않는다.**
 * 우리가 통장을 볼 일이 없기 때문이고, 그것이 자동확인과의 유일한 차이다.
 */
export const ManualAccountModal: React.FC<{
  companyId: number;
  /** 없으면 새로 등록 */
  account?: BankTransferEntity;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}> = ({ companyId, account, onClose, onSaved }) => {
  const [bank, setBank] = useState(account?.bank ?? BANKS[0].value);
  const [accountNumber, setAccountNumber] = useState(
    account?.accountNumber ?? "",
  );
  const [accountHolder, setAccountHolder] = useState(
    account?.accountHolder ?? "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const digits = accountNumber.replace(/\D/g, "");
  const canSubmit = digits.length > 0 && accountHolder.trim().length > 0;

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const command = {
        bank,
        accountNumber: digits,
        accountHolder: accountHolder.trim(),
        // 등록하자마자 결제창에 보이는 편이 자연스럽다. 감추는 건 목록에서 토글로 한다.
        isEnabled: account?.isEnabled ?? true,
      };
      if (account) {
        await companyBankTransferUseCase.updateBankTransfer(
          companyId,
          account.id,
          command,
        );
      } else {
        await companyBankTransferUseCase.createBankTransfer(companyId, command);
      }
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "계좌를 저장하지 못했습니다.");
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose}>
      <div className="w-full max-w-[420px] overflow-hidden rounded-[12px] bg-white">
        <div className="flex items-center gap-3 border-b border-[#EEEEEE] px-6 py-5">
          <div>
            <h2 className="text-base font-bold text-[#222222]">
              {account ? "계좌 수정" : "계좌 등록"}
            </h2>
            <p className="mt-0.5 text-xs text-[#888888]">
              고객에게 보여줄 계좌 정보만 받습니다.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto rounded-lg bg-[#F5F5F5] p-2 text-[#888888]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          {error && (
            <div className="rounded-lg border border-[#F24A4A]/20 bg-[#FEF2F2] px-3 py-2 text-[13px] text-[#F24A4A]">
              {error}
            </div>
          )}

          <Field label="은행">
            <select
              value={bank}
              onChange={(e) => setBank(e.target.value as typeof bank)}
              className={inputClass}
            >
              {BANKS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="계좌번호">
            <input
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="숫자만 입력"
              inputMode="numeric"
              className={inputClass}
            />
          </Field>

          <Field label="예금주">
            <input
              value={accountHolder}
              onChange={(e) => setAccountHolder(e.target.value)}
              placeholder="통장에 적힌 이름 그대로"
              className={inputClass}
            />
          </Field>
        </div>

        <div className="flex gap-2 border-t border-[#EEEEEE] px-6 py-4">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-[#222222] py-2.5 text-sm font-medium text-[#222222] hover:bg-[#F5F5F5]"
          >
            취소
          </button>
          <button
            onClick={() => void submit()}
            disabled={!canSubmit || submitting}
            className="flex-1 rounded-lg bg-[#222222] py-2.5 text-sm font-medium text-white hover:bg-[#333333] disabled:opacity-40"
          >
            저장
          </button>
        </div>
      </div>
    </ModalShell>
  );
};
